import React from 'react';
import compose from 'lodash/fp/compose';
import { withCookies } from '@catalogo/core-connect/cookies';
import { useCart } from '../hooks/cart';
import { useAddToCart } from '../hooks/add-to-cart';
import { CART_KEY, addProductToCart, getOfferId, getProductId } from '../helpers/add-to-cart';
import { IOffer } from '../types/global';
import { IWithAddToCartProps, IWithAddToCartInjectedProps } from '../types/add-to-cart';

export const withAddToCart = (Component: React.FC<IWithAddToCartProps & IWithAddToCartInjectedProps>) => (
  props: Omit<IWithAddToCartProps, keyof IWithAddToCartInjectedProps>
) => {
  const { cookies, setCookie, product: productFromProps, currentSku, productCache } = props;

  // Recupera os dados do cart
  const cartId = cookies?.[CART_KEY];
  const { data: cartData, refetch: refetchCart } = useCart(cartId);
  const cartLines = cartData?.cart?.lines;

  // Recupera o OfferId do produto
  const offerId = getOfferId(productFromProps, productCache, currentSku);

  // Recupera o productId
  const productId = getProductId(productFromProps, productCache);

  // Recupera (se existir) o cartLine baseado na offerId
  const productCartLine = cartData?.cart?.lines?.find(({ offer }: IOffer) => offer?.id === offerId);
  const quantityInCart = productCartLine?.offer?.quantity ?? 0;

  // Chama o hook com as mutations de adicao/atualizacao ao cart
  const {
    addProductQuery,
    updateProductQuery,
    loadingAddProduct,
    errorAddProduct,
    loadingUpdateProduct,
    errorUpdateProduct,
    isAddedToCart,
    isUpdatedToCart,
  } = useAddToCart({
    cookies,
    setCookie,
    refetchCart,
  });

  // Define "estado" de loading e error considerando as 2 mutations
  const loading = loadingAddProduct || loadingUpdateProduct;
  const error = errorAddProduct || errorUpdateProduct;

  // Busca a função de adicao/atualizacao do(s) produto(s) no cart
  const addToCart = addProductToCart({
    addProductQuery,
    updateProductQuery,
    cartId,
    cookies,
    productId,
  });

  return (
    <Component
      cartId={cartId}
      cartLines={cartLines}
      addToCart={addToCart}
      quantityInCart={quantityInCart}
      addToCartLoading={loading}
      addToCartError={error}
      isAddedToCart={isAddedToCart}
      isUpdatedToCart={isUpdatedToCart}
      {...props}
    />
  );
};

export default compose(withCookies, withAddToCart);
